/**
 * ErrorBoundary global — atrapa errores de render y muestra una pantalla de
 * recuperación.
 *
 * Es justo el momento en que más sirve reportar y en el que desaparecen las
 * barras superiores (con su botón), así que la pantalla de error trae su propio
 * "Reportar este error". Además `componentDidCatch` manda el error al buffer
 * del reporte: en producción React NO propaga a `window.onerror` los errores de
 * render que atrapa un boundary.
 *
 * Vive fuera de `ToastProvider` y del router: el canal se decide aquí con
 * `window.location` (el botón del kit también la toma de ahí sin router).
 */
import { Component, type ReactNode } from 'react'
import { ReportarProblemaButton, recordJsError } from '../soporte'
import { soportePorRuta } from '../lib/soporte'

interface Estado {
  hasError: boolean
  message: string
}

export default class ErrorBoundary extends Component<{ children: ReactNode }, Estado> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, message: '' }
  }

  static getDerivedStateFromError(err: Error): Estado {
    return { hasError: true, message: err.message }
  }

  componentDidCatch(error: Error) {
    recordJsError(`${error.name}: ${error.message}`)
  }

  render() {
    if (this.state.hasError) {
      const soporte = soportePorRuta(window.location.pathname)
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-lg font-semibold text-red-600 dark:text-red-400">
            Ocurrió un error inesperado
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            La aplicación encontró un problema al mostrar esta pantalla. Intenta recargar; si
            persiste, repórtalo para que el equipo lo revise.
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 font-mono max-w-xl wrap-break-word">
            Detalle técnico: {this.state.message}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => window.location.reload()} className="btn-secondary text-sm">
              Recargar página
            </button>
            {soporte && (
              <ReportarProblemaButton variant="inline" label="Reportar este error" config={soporte} />
            )}
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
